import type { KeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import {
  CAMPAIGN_TABLE_COLUMNS,
  type CampaignTableColumnId,
} from "@/components/campaign/campaign-table-layout";
import { cn } from "@/lib/utils";

interface CampaignColumnResizeHandleProps {
  columnId: CampaignTableColumnId;
  width: number;
  ariaLabel: string;
  onResize: (columnId: CampaignTableColumnId, width: number) => void;
  onReset: (columnId: CampaignTableColumnId) => void;
}

export function CampaignColumnResizeHandle({
  columnId,
  width,
  ariaLabel,
  onResize,
  onReset,
}: CampaignColumnResizeHandleProps) {
  const column = CAMPAIGN_TABLE_COLUMNS.find((candidate) => candidate.id === columnId);
  if (!column) return null;

  function handlePointerDown(event: ReactPointerEvent<HTMLHRElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = width;

    const handlePointerMove = (moveEvent: PointerEvent) => {
      onResize(columnId, startWidth + moveEvent.clientX - startX);
    };
    const stopDragging = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopDragging);
      window.removeEventListener("pointercancel", stopDragging);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("pointercancel", stopDragging);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLHRElement>) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const direction = event.key === "ArrowLeft" ? -1 : 1;
    onResize(columnId, width + direction * (event.shiftKey ? 24 : 8));
  }

  return (
    <hr
      aria-label={ariaLabel}
      aria-orientation="vertical"
      aria-valuemin={column.minWidth}
      aria-valuemax={column.maxWidth}
      aria-valuenow={width}
      tabIndex={0}
      className={cn(
        "group/resize absolute -right-1 top-0 z-40 m-0 h-full w-2 cursor-col-resize touch-none select-none border-0 outline-none",
        "before:absolute before:inset-y-0 before:left-1/2 before:w-px before:-translate-x-1/2 before:bg-line before:transition-colors",
        "hover:before:bg-brand focus-visible:before:bg-brand",
      )}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      onDoubleClick={() => onReset(columnId)}
    />
  );
}
